import React from 'react';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { StackedDialog } from '../../utilities/ModalStackContext';

const money = (amount) => `$${Number(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// A totals row. `strong` is for the figures a customer reads first.
function Total({ label, value, strong = false, hide = false }) {
  if (hide) return null;
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
      <Typography variant="body2" sx={{ fontWeight: strong ? 700 : 400, color: strong ? 'text.primary' : 'text.secondary' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: strong ? 700 : 500 }}>{money(value)}</Typography>
    </Box>
  );
}

function LineTable({ title, columns, rows, renderRow }) {
  if (!rows || !rows.length) return null;
  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
        {title} <Typography component="span" variant="caption" sx={{ color: 'text.secondary' }}>({rows.length})</Typography>
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            {columns.map((c, i) => (
              <TableCell key={c.label} align={c.align || 'left'} sx={{ fontWeight: 700, py: 0.5, pl: i === 0 ? 0 : undefined }}>
                {c.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>{rows.map(renderRow)}</TableBody>
      </Table>
    </Box>
  );
}

// What the customer actually saw on one sent document.
//
// Everything here comes from the send's own snapshot, never from the order's
// current state. That is the entire point: the estimate may have been edited or
// re-priced since, and this has to keep showing what was approved.
export default function SendSnapshotDialog({ open, onClose, send }) {
  const snapshot = send && send.snapshot;
  const totals = (snapshot && snapshot.totals) || {};
  const label = (send && send.documentLabel) || 'Document';

  return (
    <StackedDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        What was on this {label.toLowerCase()}
        <IconButton size="small" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {!snapshot ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', py: 3, textAlign: 'center' }}>
            This send has no line snapshot. Only sends made after document tracking was added record one.
          </Typography>
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <Chip size="small" label={label} />
              {send.sentAtDisplay && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>Sent {send.sentAtDisplay}</Typography>
              )}
              {snapshot.laborRate != null && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>· Labor {money(snapshot.laborRate)}/hr</Typography>
              )}
              {send.snapshotMatchesClient === false && (
                <Chip size="small" variant="outlined" label="Totals differed from the screen" sx={{ color: 'warning.dark', borderColor: 'warning.main' }} />
              )}
            </Box>

            <LineTable
              title="Services"
              columns={[{ label: 'Service' }, { label: 'Tag' }, { label: 'Hours', align: 'right' }, { label: 'Charge', align: 'right' }]}
              rows={snapshot.services}
              renderRow={(s) => (
                <TableRow key={s.id}>
                  <TableCell sx={{ pl: 0 }}>{s.name || '—'}</TableCell>
                  <TableCell>{s.tag || '—'}</TableCell>
                  <TableCell align="right">{s.hours}</TableCell>
                  <TableCell align="right">{money(s.charge)}</TableCell>
                </TableRow>
              )}
            />

            <LineTable
              title="Parts"
              columns={[{ label: 'Part' }, { label: 'Tag' }, { label: 'Qty', align: 'right' }, { label: 'Charge', align: 'right' }]}
              rows={snapshot.parts}
              renderRow={(p) => (
                <TableRow key={p.id}>
                  <TableCell sx={{ pl: 0 }}>{p.memo || p.name || '—'}</TableCell>
                  <TableCell>{p.tag || '—'}</TableCell>
                  <TableCell align="right">{p.quantity}</TableCell>
                  <TableCell align="right">{money(p.charge)}</TableCell>
                </TableRow>
              )}
            />

            <LineTable
              title="Subcontracted work"
              columns={[{ label: 'Work' }, { label: 'Vendor' }, { label: 'Charge', align: 'right' }]}
              rows={snapshot.subcontracts}
              renderRow={(s) => (
                <TableRow key={s.id}>
                  <TableCell sx={{ pl: 0 }}>{s.name || '—'}</TableCell>
                  <TableCell>{s.vendor || '—'}</TableCell>
                  <TableCell align="right">{money(s.charge)}</TableCell>
                </TableRow>
              )}
            />

            {!snapshot.services?.length && !snapshot.parts?.length && !snapshot.subcontracts?.length && (
              <Typography variant="body2" sx={{ color: 'text.secondary', py: 2 }}>
                No lines were on this document.
              </Typography>
            )}

            <Divider sx={{ my: 2 }} />

            <Box sx={{ maxWidth: 320, ml: 'auto' }}>
              <Total label="Service total" value={totals.serviceTotal} />
              <Total label="Parts total" value={totals.partsTotal} />
              <Total label="Subcontract total" value={totals.subcontractTotal} hide={!totals.subcontractTotal} />
              <Total label="Subtotal" value={totals.subtotal} />
              <Total label="Shop supplies" value={totals.shopSupplies} hide={!totals.shopSupplies} />
              <Total label="Waste fee" value={totals.wasteFee} hide={!totals.wasteFee} />
              <Total label="Discount" value={totals.discount} hide={!totals.discount} />
              <Total label={`Sales tax${totals.taxRate ? ` (${totals.taxRate}%)` : ''}`} value={totals.tax} hide={!totals.tax} />
              <Divider sx={{ my: 0.75 }} />
              <Total label="Total" value={totals.total} strong />
              <Total label="Payments" value={totals.payments} hide={!totals.payments} />
              <Total label="Balance due" value={totals.balanceDue} strong hide={!totals.payments} />
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </StackedDialog>
  );
}
